import { faDownload, faTrashCan } from '@fortawesome/pro-light-svg-icons';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  Typography
} from '@mui/material';
import { useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import { ExportButton } from '@/components/ExportButton';
import FileUploader from '@/components/FileUploader';
import { ACCEPTED_FILES_CSV } from '@/constants/common';

import {
  useDeleteAssetMapping,
  useFetchAssetMapping,
  useUploadAssetMapping
} from './AssetMappingUploadDialog.store';
import useStyles from './AssetMappingUploadDialog.styles';
import type {
  AssetMappingUpload,
  AssetMappingUploadDialogProps
} from './AssetMappingUploadDialog.types';
import { downloadExampleMappingCSV } from './AssetMappingUploadDialog.utils';

const AssetMappingUploadDialog = ({
  open,
  onClose
}: AssetMappingUploadDialogProps) => {
  const { classes } = useStyles();

  const { mutate: deleteAssetMapping, isPending: isDeleting } =
    useDeleteAssetMapping();
  const { data: existingMappings, hasData } = useFetchAssetMapping();
  const {
    mutate: uploadAssetMapping,
    error: uploadError,
    reset: resetUploadError
  } = useUploadAssetMapping();

  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);

  const form = useForm({
    defaultValues: { attachments: [] }
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting }
  } = form;

  const onSubmit = async (data: AssetMappingUpload) => {
    resetUploadError();
    uploadAssetMapping(data, {
      onSuccess: () => {
        onClose();
        reset();
      }
    });
  };

  const handleCancel = () => {
    reset(); // Reset form to default values
    resetUploadError();
    onClose();
  };

  const handleDelete = () => {
    deleteAssetMapping();
    setOpenDeleteConfirm(false);
  };

  const mapCsvData = (data: Record<string, unknown>[]) => {
    const mapped = data.map((item) => {
      // Handle tags - they might be split or already combined
      let { tags } = item;
      if (Array.isArray(item.tags)) {
        tags = item.tags.join(',');
      }

      return {
        ip_address: item.ip_address,
        tags
      };
    });

    return mapped;
  };

  return (
    <Dialog open={open} maxWidth="md" fullWidth>
      <DialogTitle>Upload IP-Tag Mapping</DialogTitle>
      <IconButton
        className={classes.assetMappingUploadDialogIconButtonCancel}
        onClick={handleCancel}
      >
        <FontAwesomeIcon size="xs" icon={faXmark} />
      </IconButton>
      <FormProvider {...form}>
        <DialogContent sx={{ padding: '1.5rem' }}>
          <Typography sx={{ mb: 2 }}>
            Upload a CSV file with two columns: <strong>ip_addresses</strong>{' '}
            and <strong>tags</strong>. Each row links an IP or range of IPs to a
            tag used for asset categorization. Supported IP formats include
            single IPs (e.g., 192.168.1.5), IP ranges
            (192.168.1.1-192.168.1.255), and CIDR notation (192.168.1.0/24).
            Once uploaded, the system automatically applies your tags to assets
            based on IP matches, helping you organize and filter assets by
            environment or purpose.
          </Typography>
          <Typography sx={{ mb: 3 }}>
            <strong>
              NOTE: Uploading a new CSV will replace your existing mapping list,
              so ensure your file includes all mappings you want to keep.
            </strong>
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Controller
              name="attachments"
              control={control}
              render={({ field }) => (
                <FileUploader
                  {...field}
                  acceptedTypes={ACCEPTED_FILES_CSV}
                  singleFile
                  errorMessage="Please upload a CSV file"
                  isRequired
                  titleButtonVariant="outlined"
                  titleText="Upload Mapping (CSV)"
                />
              )}
            />
            <Typography variant="body2" sx={{ mt: 2 }}>
              Click{' '}
              <Button
                className={classes.assetMappingUploadDialogLinkButton}
                onClick={downloadExampleMappingCSV}
              >
                here
              </Button>{' '}
              to download an example CSV
            </Typography>
          </Box>
          {hasData ? (
            <Box className={classes.assetMappingUploadDialogDownloadSection}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Current uploaded IP-Tag mapping file contains{' '}
                <strong>{existingMappings?.length || 0} entries</strong>.
              </Typography>
              <Stack direction="row" spacing={2}>
                <ExportButton
                  buttonIcon={faDownload}
                  buttonLabel="Download"
                  data={existingMappings}
                  fileName="ip-tag-mapping"
                  csv
                  mapCsvData={mapCsvData}
                  variant="outlined"
                />
                <Button
                  color="error"
                  disabled={isDeleting}
                  startIcon={<FontAwesomeIcon icon={faTrashCan} size="xs" />}
                  sx={{ px: 2 }}
                  onClick={() => setOpenDeleteConfirm(true)}
                >
                  Delete
                </Button>
              </Stack>
            </Box>
          ) : (
            <Box className={classes.assetMappingUploadDialogDownloadSection}>
              <Typography variant="body2" color="text.secondary">
                No IP-Tag mapping file has been uploaded for this organization
                yet.
              </Typography>
            </Box>
          )}
          {uploadError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {(uploadError.response?.data as { detail?: string })?.detail ||
                'Failed to upload IP tag mapping. Please check your file and try again.'}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: '1rem 1.5rem' }}>
          <Button onClick={handleCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            variant="contained"
            disabled={isSubmitting}
          >
            Upload
          </Button>
        </DialogActions>
      </FormProvider>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteConfirm}
        onClose={() => setOpenDeleteConfirm(false)}
        maxWidth="sm"
      >
        <DialogTitle>Delete IP-Tag Mapping</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mt: 1 }}>
            Are you sure you want to delete all IP-Tag mappings for this
            organization? This action cannot be undone and will remove all{' '}
            <strong>{existingMappings?.length || 0} entries</strong> along with
            their associated asset tags.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteConfirm(false)}>Cancel</Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            disabled={isDeleting}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default AssetMappingUploadDialog;
